import { useEffect, useState } from 'react'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import type { Channel } from '@/lib/types'
import { PlatformLogo, platformLogoRadius } from './PlatformLogo'

const sizes = { xs: 22, sm: 28, md: 36, lg: 48 }

export function channelProfileSource(channel: Channel) {
  return (
    channel.avatarUrl ||
    `${import.meta.env.VITE_API_BASE || ''}/api/data/channels/${channel.id}/avatar`
  )
}

export function ProfileImage({
  src,
  name,
  background,
  size,
}: {
  src?: string | null
  name: string
  background: string
  size: number
}) {
  const [failed, setFailed] = useState(false)

  useEffect(() => setFailed(false), [src])

  return (
    <span
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-full font-display font-semibold text-white"
      style={{ width: size, height: size, background, fontSize: size * 0.4 }}
      aria-label={`${name} profile picture`}
      data-avatar-source={src?.startsWith('blob:') ? 'proxied' : src ? 'remote' : 'fallback'}
    >
      {name.slice(0, 1).toUpperCase()}
      {src && !failed && (
        <img
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  )
}

export function PlatformBadge({
  platform,
  size = 14,
}: {
  platform: PlatformId
  size?: number
}) {
  return (
    <span className="inline-flex bg-surface p-0.5" style={{ borderRadius: platformLogoRadius(platform) }}>
      <PlatformLogo platform={platform} size={size + 4} />
    </span>
  )
}

export function ChannelAvatar({
  channel,
  size = 'md',
  showBadge = true,
}: {
  channel: Channel
  size?: keyof typeof sizes
  showBadge?: boolean
}) {
  const dim = sizes[size]
  const p = PLATFORMS[channel.platform]
  return (
    <div className="relative shrink-0" style={{ width: dim, height: dim }}>
      <ProfileImage
        src={channelProfileSource(channel)}
        name={channel.name}
        background={channel.avatarColor || p.gradient}
        size={dim}
      />
      {showBadge && (
        <span
          className="absolute -bottom-1 -right-1 flex bg-surface p-0.5"
          style={{ borderRadius: platformLogoRadius(channel.platform) }}
        >
          <PlatformLogo platform={channel.platform} size={Math.round(dim * 0.42)} />
        </span>
      )}
    </div>
  )
}
